import { useState } from "react";

import ArrowLeftIcon from "@heroicons/react/24/solid/ArrowLeftIcon";
import { SliceZone } from "@prismicio/react";
import qs from "qs";
import { FieldValues, SubmitHandler, useFormContext } from "react-hook-form";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import TextButton from "components/button/textButton/TextButton";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import TabbedForm from "components/form/tabbedForm/TabbedForm";
import {
  FormDocument,
  PreviewDocument,
  ProductDocumentDataCreationFlowStepsItem,
} from "prismicio-types";
import { useGetProduct, useSetCustomisation } from "queries/product-queries";
import { components as fieldComponents } from "slices/fields";
import { components as previewComponents } from "slices/previews";
import { ErrorItem, SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  usePartialTocValidation,
  getPrismicKeyFromErrorType,
  useGetTabsFromFormDocument,
} from "utils/schema-utils";

import styles from "./CreationFlowStep.module.scss";

type CreationFlowStepProps = {
  currentStep: number;
  data: ProductDocumentDataCreationFlowStepsItem;
  numberOfSteps: number;
  onBack: () => void;
  onSuccess: (arg: qs.ParsedQs) => void;
};

export default function CreationFlowStep({
  currentStep,
  data,
  numberOfSteps,
  onBack,
  onSuccess,
}: CreationFlowStepProps) {
  const methods = useFormContext();

  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const validate = usePartialTocValidation();
  const interpolate = useInterpolate();

  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const formDocument = (data as any).form as FormDocument;

  const preview =
    (formDocument.data.preview as unknown as PreviewDocument).data ?? null;
  const tabs = useGetTabsFromFormDocument(formDocument);

  const { mutate: setCustomisation } = useSetCustomisation();

  // Set validation errors in RHF, using messages from the Canvas Prismic document
  function setErrors(errors: ErrorItem[]) {
    errors.forEach((error) => {
      methods.setError(error.context.label, {
        type: error.type,
        message: product!.content.canvas[
          getPrismicKeyFromErrorType(error.type)
        ] as string,
      });
    });
  }

  const validateSubmission = async (data: FieldValues) => {
    // Run local validation first
    if (!(await methods.trigger(undefined, { shouldFocus: true }))) {
      return false;
    }

    setStatus("loading");

    try {
      const { combinedCustomisation, errors } = await validate(data);

      if (errors) {
        /**
         * Active errors fall here.
         * These errors are related to the currently active form fields.
         */
        setErrors(errors);
        return false;
      }

      return combinedCustomisation;
    } catch (error) {
      /**
       * Error isn't validation-related.
       *
       * We don't want to close the form because this could be
       * a client-side (e.g. network) or temporary API issue (e.g. timeout)
       * that a retry would fix.
       */
      setStatus("error");
      return false;
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const combinedCustomisation = await validateSubmission(data);

    if (combinedCustomisation) {
      if (tabs && activeTabIndex < tabs.length - 1) {
        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex((t) => t + 1);
        setStatus("idle");
      } else {
        onSuccess(combinedCustomisation);
      }
    }
  };

  async function onTabChange(index: number) {
    if (index === activeTabIndex) {
      return;
    }

    methods.handleSubmit(async (data) => {
      const combinedCustomisation = await validateSubmission(data);

      if (combinedCustomisation) {
        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex(index);
        setStatus("idle");
      }
    })();
  }

  async function handleOnBack() {
    if (currentStep === 1) {
      onBack();
      setStatus("loading");
    } else if (!tabs || activeTabIndex === 0) {
      await methods.handleSubmit(async (data) => {
        const combinedCustomisation = await validateSubmission(data);

        if (combinedCustomisation) {
          setCustomisation({ data: combinedCustomisation });
          onBack();
          setStatus("idle");
        }
      })();
    } else {
      onTabChange(activeTabIndex - 1);
    }
  }

  return (
    <>
      <div className={styles.stepTop}>
        <h1 className={styles.stepCountLabel}>
          {interpolate(
            product.content.canvas.creation_flow_step_count_label ??
              CANVAS_FALLBACK_CONTENT.creation_flow_step_count_label,
            {
              currentStep,
              numberOfSteps,
            }
          )}
        </h1>
        {data.title && (
          <h2 className={styles.stepTitle}>{interpolate(data.title)}</h2>
        )}
      </div>

      {preview && (
        <ErrorBoundary>
          <SliceZone components={previewComponents} slices={preview.slices} />
        </ErrorBoundary>
      )}

      <div className={styles.content}>
        <form
          aria-label={formDocument.data.title || ""}
          className={styles.form}
          onSubmit={(event) => {
            event.stopPropagation();
            methods.handleSubmit(onSubmit)(event);
          }}
        >
          <div className={styles.fields}>
            {tabs ? (
              <TabbedForm
                activeTabIndex={activeTabIndex}
                onTabChange={onTabChange}
                tabs={tabs}
              />
            ) : (
              <ErrorBoundary>
                <SliceZone
                  components={fieldComponents}
                  slices={formDocument.data.slices}
                />
              </ErrorBoundary>
            )}
          </div>

          <div className={styles.stickyActions}>
            <PrimaryButton isLoading={status === "loading"} type="submit">
              {status === "loading"
                ? interpolate(
                    product.content.canvas.form_loading_label ??
                      CANVAS_FALLBACK_CONTENT.form_loading_label
                  )
                : interpolate(
                    product.content.canvas.creation_flow_submit_button_label ??
                      CANVAS_FALLBACK_CONTENT.creation_flow_submit_button_label
                  )}
            </PrimaryButton>

            <TextButton
              className={styles.backButton}
              color="gray"
              onClick={handleOnBack}
              type="button"
            >
              <ArrowLeftIcon className={styles.backIcon} />
              {currentStep === 1
                ? interpolate(
                    product.content.canvas.back_product_page_link ??
                      CANVAS_FALLBACK_CONTENT.back_product_page_link
                  )
                : interpolate(
                    product.content.canvas.back_previous_step_link ??
                      CANVAS_FALLBACK_CONTENT.back_previous_step_link
                  )}
            </TextButton>
          </div>
        </form>
      </div>
    </>
  );
}
